import { ClickEvent, CloneComponent, Component, Image, Label, Panel, PanelProps, Textbox } from "@mcleod/components";
import { AutogenLayoutMovementTabs } from "./autogen/AutogenLayoutMovementTabs";
import { CompanySettings, ModelRow, StringUtil } from "@mcleod/core";
import { ImageName } from "@mcleod/images";

const defaultTabProps = {
    backgroundColor: "subtle.lightest",
    zIndex: 0
}

const defaultPostedTabProps = {
    backgroundColor: "subtle.light",
    color: "subtle.darkest",
    zIndex: 0
}

const activeTabProps = {
    backgroundColor: "backgroundSubtle",
    zIndex: 10
}

interface TabTopperProps {
    text: string;
    color: string;
    animate?: boolean;
}

interface MovementTab {
    movement: ModelRow;
    tab: Panel;
    tabTopper: Panel;
    tabInfo: Panel;
}

interface MovementTabProps {
    overrideActiveTab?: boolean;
    tabStyleProps?: Partial<PanelProps>;
    hideOrder?: boolean;
    tabSpacer?: Component;
}

export class MovementTabs extends AutogenLayoutMovementTabs {

    public currentTabId: string;
    public selectedMovementId: string;
    public movementTabOnAdd: (movement: ModelRow) => void = () => {};
    public movementTabOnUpdate: (movement: ModelRow) => void = () => {};
    public doBeforeTabClick: (movement: ModelRow) => Promise<boolean>;
    public movementTabOnClickCallback: (movement: ModelRow, newTab?: Panel, oldTab?: Panel) => void = () => {};
    public getTab: () => MovementTab = () => null;
    public movements: ModelRow[] = [];
    private tabProps: MovementTabProps;
    private observer: IntersectionObserver;

    onLoad() {
        this.panelMovementTabInfo.style.overflowX = "hidden";
        this.panelMovementTabInfo.element.onscrollend = () => this.checkBoundaries();
        this.observer = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting) {
                this.checkBoundaries();
            }
        }, { root: this.panelMovementTabInfo.element });
        this.setBottomBorderStyling();
    }

    addTabProps(props: MovementTabProps) {
        this.tabProps = props;
    }

    addMovementTabs(movements: ModelRow[]) {
        this.movements = movements;
        movements.forEach((movement, index) => {
            const newTab = CloneComponent.clone({
                id: movement.get("movement_id", movement.get("id")),
                component: this.panelMovementTab
            }) as Panel;
            this.panelMovementTabInfo.add(newTab);

            if (this.tabProps?.tabSpacer != null) {
                const tabSpacer = CloneComponent.clone({
                    id: movement.get("movement_id", movement.get("id")) + "-tabSpacer",
                    component: this.tabProps.tabSpacer
                });
                this.panelMovementTabInfo.add(tabSpacer);
            }

            this.setupMovementTab(movement,index == 0);
            this.movementTabOnAdd(movement);
            newTab.addClickListener((event: ClickEvent) => this.movementTabOnClick(newTab, movement));
            this.observer.observe(newTab._element);
            this.updateTabStop(movement);
        });
        this.scrollToCurrent();
    }

    async movementTabOnClick(movementTab: Panel, movement: ModelRow) {
        const movementId = movement.get("movement_id", movement.get("id"));
        if (movementId != this.currentTabId) {
            if (await this.doBeforeTabClick?.(movement) === false) {
                return;
            }
            let oldTab: Panel;
            if (this.currentTabId != null) {
                oldTab = this.panelMovementTabInfo.findComponentById(this.currentTabId) as Panel;
                if (oldTab != null) {
                    const oldTabInfo = (oldTab.findComponentById(`${this.currentTabId}-panelTabInfo`) as Panel);
                    oldTabInfo.setProps(defaultTabProps);
                }
            }
            this.currentTabId = movementId;
            const tabInfo = (movementTab.findComponentById(`${movementId}-panelTabInfo`) as Panel);
            tabInfo.setProps(activeTabProps);
            this.updateTabStops();
            this.scrollToTab(movementTab);
            this.movementTabOnClickCallback(movement, movementTab, oldTab);
        }
    }

    getMovementTab(movement: ModelRow): MovementTab {
        const movementId = movement.get("movement_id", movement.get("id"));
        const tab = this.panelMovementTabInfo.findComponentById(movementId) as Panel;
        if (tab == null) return null;
        return {
            movement,
            tab,
            tabInfo: tab.findComponentById(`${movementId}-panelTabInfo`) as Panel,
            tabTopper: tab.findComponentById(`${movementId}-panelTabTopper`) as Panel
        };
    }

    getTabSpacer(movement_id: string) {
        return this.panelMovementTabInfo.findComponentById(`${movement_id}-tabSpacer`) as Component;
    }

    setupMovementTab(movement: ModelRow, firstTab?: boolean) {
        const movementId = movement.get("movement_id", movement.get("id"));
        const movementTab = this.getMovementTab(movement);
        movementTab.tab.visible = true;
        movementTab.tabInfo.setProps(defaultTabProps);

        if (this.tabProps?.tabStyleProps) {
            movementTab.tabInfo.setProps(this.tabProps.tabStyleProps);
        }
        if (this.movements.length == 1 || (this.currentTabId == null && movementId == this.selectedMovementId)
            || movementId == this.currentTabId) {
            this.currentTabId = movementId;
            movementTab.tabInfo.setProps(activeTabProps);
        }
        this.setStatusIcon(this.getTabComp(movementTab, "imageStatus") as Image, movement.get("status"), movement.get("loaded"), movement.getBoolean("is_locate_move"));
        const orderNumber = (this.getTabComp(movementTab, "textboxOrderNumber") as Textbox);
        const labelOrderNumber = (this.getTabComp(movementTab, "labelOrderNumber") as Textbox);
        orderNumber.text = movement.get("manifest_id") ? movement.get("manifest_id") : movement.get("order_id");
        orderNumber.caption = movement.get("manifest_id") ? "Manifest Number" : "Order Number";
        if (this.tabProps?.hideOrder) {
            orderNumber.visible = false;
            labelOrderNumber.visible = false;
        }
        this.setMoveTypeBadge(movementTab, movement);
    }

    setMoveTypeBadge(movementTab: MovementTab, movement: ModelRow) {
        const moveTypeBadge = (this.getTabComp(movementTab, "labelMovementType") as Textbox);
        if (this.tabProps?.hideOrder) {
            moveTypeBadge.rowBreak = false;
            return;
        }
        if (CompanySettings.getSingleton().isAsset() && !CompanySettings.getSingleton().isBrokerage()) {
            moveTypeBadge.text = movement.getBoolean("brokerage", false) ? "Brokerage" : null;
            moveTypeBadge.visible = !StringUtil.isEmptyString(moveTypeBadge.text);
        }
        else if (CompanySettings.getSingleton().isBoth()) {
            moveTypeBadge.text = movement.getBoolean("brokerage", false) ? "Brokerage" : "Asset";
            moveTypeBadge.visible = true;
        }
    }


    getStopString(movement: ModelRow, firstCity: boolean, includeCityState: boolean) {
        const hideOrderId: boolean = this.tabProps?.hideOrder;
        const cityField = firstCity ? "first_city" : "last_city";
        const stateField = firstCity ? "first_state" : "last_state";
        const city = movement.get(cityField);
        const state = movement.get(stateField);
        if (!StringUtil.isEmptyString(city) && !StringUtil.isEmptyString(state)) {
            return includeCityState ?
            `${city}, ${state} to${hideOrderId ? "\n" : " "}${movement.get("last_city")}, ${movement.get("last_state")}` :
            `${state}`;
        }
        return null;
    }

    hasSelectedMovement() {
        return this.movements.some(m => m.get("movement_id", m.get("id")) == this.selectedMovementId);
    }

    showTabTopper(movement: ModelRow, movementTab: Panel, props: TabTopperProps) {
        const movementId = movement.get("movement_id", movement.get("id"));
        const tabTopper = (movementTab.findComponentById(`${movementId}-panelTabTopper`) as Panel);
        (tabTopper.components[0] as Label).text = props.text;
        tabTopper.backgroundColor = props.color;
        tabTopper.visible = true;
        tabTopper.style.position = "absolute";
        this.slideTabTopper(tabTopper, true);
    }

    slideTabTopper(tabTopper: Panel, slideIn: boolean, animate: boolean = true) {
        tabTopper.style.transition = "top 0.5s ease";
        tabTopper.style.top = `${slideIn ? "101%" : "0"}`;
        setTimeout(() => {
            tabTopper.style.top = `${slideIn ? "0%" : "101%"}`;
        }, (animate ? 100 : 0));
    }

    setStatusIcon(statusImage: Image, status: string, loaded: string, isLocateMove: boolean) {
        const isEmpty = loaded == "E";
        let imageName: ImageName;
        let tooltip: string;
        switch (status) {
            case "A": {
                imageName = ImageName.loadAvailable;
                tooltip = "Available";
                break;
            }
            case "C": {
                imageName = ImageName.loadCovered;
                tooltip = "Covered";
                break;
            }
            case "P": {
                imageName = ImageName.loadInProgress;
                tooltip = "In Progress";
                break;
            }
            case "V": {
                imageName = ImageName.loadVoided;
                tooltip = "Void";
                break;
            }
            case "D": {
                imageName = ImageName.loadDelivery;
                tooltip = "Delivered";
                break;
            }
        }
        statusImage.name = (isEmpty && status !== "V") ? isLocateMove ? ImageName.locateMove : ImageName.emptyMove : imageName;
        statusImage.tooltip = (isEmpty && status !== "V") ? isLocateMove ? "Locate Move" : "Empty Move" : tooltip;
        if (isEmpty) {
            statusImage.color = "subtle";
        }
    }

    manageDispatchToppers(shouldDispatch: boolean, isPrevious: boolean, nextTab: boolean, isEmpty: boolean, movementTab: MovementTab) {
        if (isPrevious) {
            this.showTabTopper(movementTab.movement, movementTab.tab, { color: "purple", text: "PREVIOUS" });
        } else if (shouldDispatch) {
            this.showTabTopper(movementTab.movement, movementTab.tab, { color: "warning", text: "CURRENT" });
        } else if (nextTab) {
            this.showTabTopper(movementTab.movement, movementTab.tab, { color: "success", text: "NEXT" });
        } else if (isEmpty) {
            this.showTabTopper(movementTab.movement, movementTab.tab, { color: "subtle", text: "EMPTY" });
        }
    }

    manageMovementToppers(loaded: boolean, movementTab: MovementTab) {
        this.showTabTopper(movementTab.movement, movementTab.tab, {
            color: loaded ? "success" : "subtle", text: loaded ? "LOADED" : "EMPTY"
        });
    }

    /** This is an event handler for the onClick event of buttonLeftNav. */
    buttonLeftNavOnClick(event: ClickEvent) {
        const element = this.panelMovementTabInfo.element;
        const firstElementWidth = this.panelMovementTabInfo.components[1].element.offsetWidth;
        this.panelMovementTabInfo.element.scrollLeft -= element.offsetWidth;
        if (element.scrollLeft - 500 < firstElementWidth) {
            this.panelMovementTabInfo.element.scrollLeft = 0;
        }
    }

    /** This is an event handler for the onClick event of buttonRightNav. */
    buttonRightNavOnClick(event: ClickEvent) {
        const element = this.panelMovementTabInfo.element;
        element.scrollLeft += element.offsetWidth;
    }

    checkBoundaries() {
        const minusIndex = this.tabProps?.tabSpacer != null ? 2 : 1;
        const tabs = this.panelMovementTabInfo.components;
        const firstTab = tabs[0] as Panel;
        const lastTab = tabs[tabs.length - minusIndex] as Panel;
        this.panelLeftNav.visible = !this.isTabVisible(firstTab);
        this.panelRightNav.visible = !this.isTabVisible(lastTab);
    }

    getTabComp(movementTab: MovementTab, comp: string) {
        const movementId = movementTab.movement.get("movement_id", movementTab.movement.get("id"));
        return movementTab.tabInfo.findComponentById(`${movementId}-panelTabInfo-${comp}`);
    }

    isTabVisible(tab: Panel) {
        return tab.bounds?.left >= this.panelMovementTabInfo.bounds?.left &&
        tab.bounds?.right-48 <= this.panelMovementTabInfo.bounds?.right;
    }

    scrollToCurrent() {
        if (!this.currentTabId) return;
        const currentTab = (this.panelMovementTabInfo.findComponentById(this.currentTabId) as Panel)?.element;
        currentTab?.scrollIntoView({ behavior: "smooth", block: "end" });
    }

    scrollToTab(tab: Panel) {
        if (!this.isTabVisible(tab)) {
            this.panelMovementTabInfo.element.scrollTo(tab.element.offsetLeft, 0);
        }
    }

    updateTabStops() {
        this.movements.forEach((movement, index) => {
          this.updateTabStop(movement);
        })
    }

    updateTabStop(movement: ModelRow) {
        const movementTab = this.getMovementTab(movement);
        const movementId = movement.get("movement_id", movement.get("id"));
        const firstCityState = this.getTabComp(movementTab, "labelFirstCityState") as Label;
        const lastCityState = this.getTabComp(movementTab, "labelLastCityState") as Label;
        const imageTo = this.getTabComp(movementTab, "imageTo") as Image;
        const activeTabs = this.getTab()?.tab == movementTab.tab || this.tabProps?.overrideActiveTab || movementId == this.currentTabId;
        const hasMultipleEmpties = !movement.isNull("empty_count");
        const hasTabOverflow = this.isTabVisible(this.panelLeftNav) || this.isTabVisible(this.panelRightNav);
        if (hasMultipleEmpties) {
            const panelEmptyMovesCount = this.getTabComp(movementTab, "panelEmptyMovesCount") as Panel;
            const labelEmptyMoveCount = panelEmptyMovesCount.components[0] as Label;
            panelEmptyMovesCount.visible = true;
            labelEmptyMoveCount.text = `${movement.get("empty_count")} Empty Moves`;
        } else {
            if (!hasTabOverflow || activeTabs) {
                firstCityState.text = this.getStopString(movement, true, true);
            } else {
                firstCityState.text = this.getStopString(movement, true, false);
                lastCityState.text = this.getStopString(movement, false, false);
            }
            firstCityState.fontSize = lastCityState.fontSize = (!hasTabOverflow || activeTabs) ? "small" : "large";
            firstCityState.visible = true;
            imageTo.visible = lastCityState.visible = hasTabOverflow && !activeTabs;
        }

        if (!movement.isNull("split_image")) {
            this.setSplitImage(movementTab, movement);
        }
        else if (!this.tabProps?.hideOrder) {
            firstCityState.marginLeft = 32;
        }

        this.movementTabOnUpdate(movement);
    }

    setSplitImage(movementTab: MovementTab, movement: ModelRow)  {
        const imageYSplit =  this.getTabComp(movementTab, "imageYSplit") as Image;
        if (imageYSplit != null) {
            imageYSplit.setProps({
                name: movement.get("split_image"),
                color: movement.get("split_image") == "ysplitPick" ? "success":"primary",
                visible: true
            });
        }
    }

    setBottomBorderStyling() {
        this.panelBorder.style.position = "absolute";
        this.panelBorder.style.bottom = "0px";
        this.panelBorder.style.width = "-webkit-fill-available";
    }
}
